import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { ApiError, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { useIsDesktop, useIsWide, useMyCommunities } from "../../lib/hooks";
import { useUI } from "../../lib/uiStore";
import { CommunityIcon } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { Badge, EmptyState, ErrorState } from "../../components/ui/Controls";
import { Input } from "../../components/ui/Input";
import { PageSpinner } from "../../components/ui/Spinner";
import { Skeleton } from "../../components/ui/Skeleton";
import { IconAdd, IconCommunities, IconExplore, IconLink, IconVolume } from "../../components/icons";
import { ChannelSidebar } from "./ChannelSidebar";
import { ChannelView } from "./ChannelView";
import { VoiceRoomView } from "./VoiceRoomView";
import { StageView } from "../voice/StageView";
import { MemberList } from "./MemberList";
import { CommunitySettings } from "./CommunitySettings";
import { ChannelSettings } from "./ChannelSettings";
import { CategorySettings } from "./CategorySettings";
import { useStructure } from "./hooks";
import { ForumView } from "../forums/ForumView";
import { OnboardingHost } from "../onboarding/OnboardingFlow";
import { PlatformBanner, SuspendedNotice } from "../staff/CommunityNotices";
import { communityAccentProps } from "./CommunityBanner";
import { CommunityRow, useDiscover } from "../feed/DiscoverSidebar";
import { UUID_RE } from "@atrium/shared";

function JoinWithInvite({ className }: { className?: string }) {
  const [value, setValue] = useState("");
  const navigate = useNavigate();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const code = value.trim().split("/").filter(Boolean).pop();
    if (code) navigate(`/invite/${code}`);
  };
  return (
    <form onSubmit={submit} className={cn("flex gap-2", className)}>
      <div className="relative flex-1">
        <IconLink className="icon pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-fg-3" aria-hidden />
        <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Paste an invite link" className="pl-9" aria-label="Invite link or code" />
      </div>
      <Button type="submit" disabled={!value.trim()}>
        Join
      </Button>
    </form>
  );
}

function CommunitiesHome() {
  const { data, isPending } = useMyCommunities();
  const discover = useDiscover();
  const setCreateOpen = useUI((s) => s.setCreateCommunityOpen);
  const suggestions = (discover.data?.recommendedCommunities.length ? discover.data.recommendedCommunities : discover.data?.popularCommunities ?? []).filter((c) => !c.isMember).slice(0, 6);

  return (
    <div className="scroll-y h-full">
      <div className={cn("grid min-h-full grid-cols-1", suggestions.length > 0 && "xl:grid-cols-[minmax(0,1fr)_minmax(340px,420px)]")}>
        <div className="min-w-0 px-4 py-6 md:px-8 md:py-8">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h1 className="text-xl font-semibold">Communities</h1>
            <div className="flex items-center gap-2">
              <JoinWithInvite className="hidden w-80 md:flex [&_input]:h-9" />
              <Link to="/explore?tab=communities">
                <Button icon={IconExplore} variant="ghost">
                  <span className="hidden sm:inline">Discover</span>
                </Button>
              </Link>
              <Button icon={IconAdd} variant="primary" onClick={() => setCreateOpen(true)}>
                Create
              </Button>
            </div>
          </div>
          <JoinWithInvite className="mb-6 md:hidden" />

          {isPending ? (
            <div className="grid gap-3 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-36 rounded-xl" />
              ))}
            </div>
          ) : data && data.length > 0 ? (
            <ul className="grid gap-1 sm:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] sm:gap-3">
              {data.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/c/${c.id}`}
                    className="flex h-full items-center gap-3 rounded-lg p-2.5 transition-colors hover:bg-surface sm:flex-col sm:items-start sm:gap-3 sm:rounded-xl sm:border sm:border-line sm:p-4 sm:hover:border-line-strong"
                  >
                    <div className="flex w-full items-start justify-between gap-3">
                      <CommunityIcon src={c.iconUrl} name={c.name} size="lg" />
                      <div className="flex items-center gap-2 sm:order-none">
                        {c.voiceCount > 0 && (
                          <span className="flex items-center gap-1 text-xs font-medium text-success tabular">
                            <IconVolume className="icon size-4" aria-hidden /> {c.voiceCount}
                          </span>
                        )}
                        {c.mentionCount > 0 ? <Badge count={c.mentionCount} /> : c.unread ? <Badge dot /> : null}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 sm:w-full">
                      <div className={cn("truncate text-base", c.unread ? "font-semibold text-fg" : "font-medium text-fg")}>{c.name}</div>
                      <div className="truncate text-xs text-fg-3">
                        {c.memberCount} {c.memberCount === 1 ? "member" : "members"}
                        {c.visibility === "private" && " · Private"}
                      </div>
                      {c.description && <p className="mt-2 line-clamp-2 hidden text-sm text-fg-2 sm:block">{c.description}</p>}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={IconCommunities}
              title="Find your people"
              description="Communities are spaces for ongoing conversation, with text channels and voice rooms."
              action={
                <div className="flex gap-2">
                  <Button variant="primary" icon={IconAdd} onClick={() => setCreateOpen(true)}>
                    Create a community
                  </Button>
                  <Link to="/explore?tab=communities">
                    <Button>Browse</Button>
                  </Link>
                </div>
              }
            />
          )}
        </div>

        {suggestions.length > 0 && (
          <aside className="border-line px-4 pb-8 md:px-8 xl:border-l xl:bg-bg-subtle xl:px-5 xl:py-8">
            <h2 className="mb-2 text-sm font-semibold text-fg-2">Suggested for you</h2>
            <div className="-mx-4 xl:-mx-5">
              {suggestions.map((c) => (
                <CommunityRow key={c.id} c={c} />
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}

function CommunityLayout({
  communityId,
  channelId,
  section,
  settingsPath,
  channelSettings,
  categorySettings,
  threadId,
}: {
  communityId: string;
  channelId?: string;
  section?: string;
  settingsPath?: string[];
  /** Set when viewing /c/:community/:channel/settings/:section? */
  channelSettings?: string[];
  /** Set when viewing /c/:community/category/:category/settings/:section? */
  categorySettings?: { id: string; path: string[] };
  /** Forum post open at /c/:community/:channel/t/:thread. */
  threadId?: string;
}) {
  const desktop = useIsDesktop();
  const wide = useIsWide();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lastChannel, setLastChannel, membersOpen } = useUI();
  const { data: structure, error, isPending, refetch } = useStructure(communityId);
  const onRoute = pathname.startsWith(`/c/${communityId}`);

  const channel = structure?.channels.find((c) => c.id === channelId);

  useEffect(() => {
    if (channel && channel.type !== "voice" && channel.type !== "stage") setLastChannel(communityId, channel.id);
  }, [channel, communityId, setLastChannel]);

  // Desktop: opening a community lands in its last (or first) text channel.
  useEffect(() => {
    if (!desktop || !structure || channelId || section || categorySettings?.id || !onRoute) return;
    const remembered = structure.channels.find((c) => c.id === lastChannel[communityId] && c.type !== "voice" && c.type !== "stage");
    const landing = structure.channels.find((c) => c.id === structure.community.defaultChannelId);
    const first = remembered ?? landing ?? structure.channels.find((c) => c.type !== "voice" && c.type !== "stage");
    if (first) navigate(`/c/${communityId}/${first.id}`, { replace: true });
  }, [desktop, structure, channelId, section, categorySettings?.id, communityId, lastChannel, navigate, onRoute]);

  if (isPending) return <PageSpinner />;
  if (error) {
    if (error instanceof ApiError && error.status === 403) return onRoute ? <Navigate to={`/community/${communityId}`} replace /> : null;
    if (error instanceof ApiError && error.status === 404)
      return <EmptyState title="Community not found" description="It may have been deleted, or you may no longer have access." action={<Link to="/c"><Button>Back to communities</Button></Link>} />;
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }
  if (!structure) return null;

  // Suspended by Atrium staff: members see only the notice; staff carry on with a banner.
  if (structure.community.platform?.status === "suspended" && !structure.community.viewerIsStaff) return <SuspendedNotice structure={structure} />;

  if (section === "settings")
    return (
      <div className="h-full" {...communityAccentProps(structure.community.accentColor)}>
        <CommunitySettings structure={structure} section={settingsPath?.[0]} sub={settingsPath?.[1]} />
      </div>
    );

  const main = channel ? (
    channel.type === "forum" || channel.type === "media" ? (
      <ForumView structure={structure} channel={channel} threadId={threadId} mobile={!desktop} />
    ) : channel.type === "voice" ? (
      <VoiceRoomView structure={structure} channel={channel} mobile={!desktop} />
    ) : channel.type === "stage" ? (
      <StageView structure={structure} channel={channel} mobile={!desktop} />
    ) : (
      <ChannelView structure={structure} channel={channel} mobile={!desktop} />
    )
  ) : channelId ? (
    <EmptyState title="Channel not found" description="It may have been deleted, or you no longer have access." className="flex-1" />
  ) : (
    <EmptyState icon={IconCommunities} title={structure.community.name} description="Pick a channel to start talking." className="flex-1" />
  );

  const accent = communityAccentProps(structure.community.accentColor);
  const category = categorySettings && structure.categories.find((c) => c.id === categorySettings.id);
  if (categorySettings)
    return category ? (
      <div className="h-full" {...accent}>
        <CategorySettings structure={structure} category={category} section={categorySettings.path[0]} />
      </div>
    ) : (
      <EmptyState title="Category not found" description="It may have been deleted." action={<Link to={`/c/${communityId}`}><Button>Back to channels</Button></Link>} />
    );
  if (channel && channelSettings)
    return (
      <div className="h-full" {...accent}>
        <ChannelSettings structure={structure} channel={channel} section={channelSettings[0]} />
      </div>
    );
  if (!desktop) {
    return channelId ? (
      <div className="flex h-full flex-col" {...accent}>
        <OnboardingHost structure={structure} />
        <PlatformBanner structure={structure} />
        <div className="flex min-h-0 flex-1">{main}</div>
      </div>
    ) : (
      <div className="flex h-full flex-col" {...accent}>
        <OnboardingHost structure={structure} />
        <div className="min-h-0 flex-1">
          <ChannelSidebar structure={structure} mobile />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full" {...accent}>
      <ChannelSidebar structure={structure} activeChannelId={channelId} />
      <div className="flex min-w-0 flex-1 flex-col">
        <OnboardingHost structure={structure} />
        <PlatformBanner structure={structure} />
        <div className="flex min-h-0 min-w-0 flex-1">{main}</div>
      </div>
      {wide && membersOpen && channel && channel.type !== "voice" && channel.type !== "stage" && <MemberList structure={structure} channelId={channel.id} />}
    </div>
  );
}

export function CommunitiesPane() {
  const { pathname } = useLocation();
  const lastCommunityPath = useUI((s) => s.lastCommunityPath);
  // Keep showing the last community view while another pane is in front.
  const path = pathname === "/c" || pathname.startsWith("/c/") ? pathname : lastCommunityPath || "/c";
  const [, , communityId, second, ...rest] = path.split("/");
  if (!communityId || !UUID_RE.test(communityId)) return <CommunitiesHome />;
  const section = second === "settings" ? "settings" : undefined;
  const categorySettings = second === "category" && rest[0] && UUID_RE.test(rest[0]) && rest[1] === "settings" ? { id: rest[0], path: rest.slice(2) } : undefined;
  const channelSettings = !section && !categorySettings && rest[0] === "settings" ? rest.slice(1) : undefined;
  const threadId = !section && rest[0] === "t" && rest[1] && UUID_RE.test(rest[1]) ? rest[1] : undefined;
  return (
    <CommunityLayout
      key={communityId}
      communityId={communityId}
      channelId={section || categorySettings ? undefined : second}
      section={section}
      settingsPath={section ? rest : undefined}
      channelSettings={channelSettings}
      categorySettings={categorySettings}
      threadId={threadId}
    />
  );
}
